import React, { useState, useEffect } from "react";

import { saveAs } from "file-saver";
import getOrderSummaryStatement from "../../api/statistic/getOrderSummaryStatement";
import getStatusStatement from "../../api/statistic/getStatusStatement";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  IconButton,
  Paper,
  Button,
  Stack,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

// Report Card Component for Statement Report
const StatementReportCard = ({ data }) => {
  return (
    <Grid container spacing={2} sx={{ marginTop: 1 }}>
      <Grid item xs={2}>
        <Typography>{data.TableName}</Typography>
      </Grid>
      <Grid item xs={2}>
        <Typography>{data.Quantity}</Typography>
      </Grid>
      <Grid item xs={2}>
        <Typography>{data.PaymentMethod}</Typography>
      </Grid>
      <Grid item xs={2}>
        <Typography>{data.Discount}</Typography>
      </Grid>
      <Grid item xs={3}>
        <Typography>{data.Subtotal}</Typography>
      </Grid>
    </Grid>
  );
};

const formatDateTime = (dateTimeStr) => {
  const date = new Date(dateTimeStr);
  return date.toLocaleString("zh-TW", { timeZone: "Asia/Taipei" }); // 'Asia/Shanghai' is one of the timezones representing GMT+8
};

const StatusDetails = ({ details }) =>
  details.map((status, index) => (
    <Grid
      container
      key={index}
      spacing={2}
      sx={{ marginTop: 1, paddingLeft: 2 }}
    >
      <Grid item xs={4}>
        <Typography color="textSecondary">{status.Status}</Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography color="textSecondary">{status.Duration} 分鐘</Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography color="textSecondary">
          {formatDateTime(status.StartTime)}
        </Typography>
      </Grid>
    </Grid>
  ));
// Function to convert data to CSV format
const convertToCSV = (objArray) => {
  const array = typeof objArray !== "object" ? JSON.parse(objArray) : objArray;
  let str = "";

  // Extract headers
  const headerLine = Object.keys(array[0]).join(",");
  str += headerLine + "\r\n";

  // Extract data rows
  for (let i = 0; i < array.length; i++) {
    let line = "";
    for (const index in array[i]) {
      if (line !== "") line += ",";

      line += array[i][index];
    }
    str += line + "\r\n";
  }

  return str;
};

// Function to trigger download
const downloadCSV = (data, filename) => {
  const csvData = new Blob([convertToCSV(data)], {
    type: "text/csv;charset=utf-8;",
  });
  saveAs(csvData, filename);
};
function SummaryReport({ show = false, storeCode, startDate, endDate }) {
  const [statementReport, setStatementReport] = useState([]);
  const [statusReport, setStatusReport] = useState([]);
  const [error, setError] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [selectedTableCode, setSelectedTableCode] = useState(null);
  const [sortedStatusReport, setSortedStatusReport] = useState([]);

  // Toggle the selected table code
  const handleRowClick = (tableCode) => {
    setSelectedTableCode(selectedTableCode === tableCode ? null : tableCode);
  };
  const handleDownloadStatement = () => {
    downloadCSV(statementReport.Tables, "sumReport.csv");
  };

  const handleDownloadStatus = () => {
    downloadCSV(sortedStatusReport?.StatusDetails, "statusReport.csv");
  };
  useEffect(() => {
    const fetchData = async () => {
      try {
        const tableData = await getOrderSummaryStatement(
          storeCode,
          startDate,
          endDate
        );
        const statusData = await getStatusStatement(
          storeCode,
          startDate,
          endDate
        );

        setStatementReport(tableData.report);
        setStatusReport(statusData.report);
        setShowAlert(true);
        setIsSuccess(true);
      } catch (err) {
        setShowAlert(true);
        setIsSuccess(false);
        setError(err.message);
      }
    };
    if (show) {
      fetchData();
    }
  }, [show, storeCode, startDate, endDate]);
  useEffect(() => {
    if (statusReport) {
      const sortedData = [...statusReport].sort((a, b) =>
        a.TableCode.localeCompare(b.TableCode)
      );
      setSortedStatusReport(sortedData);
    }
  }, [statusReport]);

  return (
    <>
      <Card sx={{ margin: "1px", marginTop: 1 }}>
        <CardContent>
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            alignContent={"center"}
          >
            <Typography variant="h5" sx={{ marginBottom: 2 }}>
              總計報表
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleDownloadStatement}
              sx={{ height: 40 }}
            >
              下載總計報表
            </Button>
          </Stack>
          <Grid container spacing={1}>
            <Grid item xs={2}>
              <Typography variant="subtitle1">
                <strong>桌次</strong>
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="subtitle1">
                <strong>總時數</strong>
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="subtitle1">
                <strong>付款方式</strong>
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="subtitle1">
                <strong>折扣</strong>
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="subtitle1">
                <strong>小計</strong>
              </Typography>
            </Grid>
          </Grid>

          {statementReport.Tables?.map((report, index) => (
            <StatementReportCard key={index} data={report} />
          ))}
          <Grid container spacing={2} sx={{ marginTop: 1 }}>


            {statementReport.PaymentTotal?.map((report, index) => (
              <Grid item xs={2}>
              <strong>{report.PaymentMethod} </strong>
              <Typography color={"black"}>{report.Total}</Typography>            </Grid>
          ))}

             

            <Grid item xs={3}>
              <strong>總計</strong>
              <Typography color={"black"}>{statementReport.Total}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <Card sx={{ margin: "1px", marginTop: 1, overflow: "hidden" }}>
        <CardContent>
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            alignContent={"center"}
          >
            <Typography variant="h5" sx={{ marginBottom: 2 }}>
              狀態報表
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleDownloadStatus}
              sx={{ height: 40 }}
            >
              下載狀態時間紀錄明細
            </Button>
          </Stack>
          {statusReport ? (
            sortedStatusReport.map((report, index) => (
              <Paper
                key={index}
                elevation={selectedTableCode === report.TableCode ? 4 : 1}
                sx={{ marginBottom: 2, padding: 1 }}
              >
                <Grid
                  container
                  alignItems="center"
                  sx={{
                    cursor: "pointer",
                    backgroundColor:
                      selectedTableCode === report.TableCode
                        ? "#ffffff"
                        : "#ffffff",
                  }}
                >
                  <Grid
                    item
                    xs={10}
                    onClick={() => handleRowClick(report.TableCode)}
                  >
                    <Typography variant="subtitle1" color="info">
                      <strong>
                        桌次 {report.TableCode} - 強制開啟時間總計:{" "}
                        {report.TotalOpenDuration} 分鐘
                      </strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={2} sx={{ textAlign: "right" }}>
                    <IconButton
                      onClick={() => handleRowClick(report.TableCode)}
                    >
                      {selectedTableCode === report.TableCode ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </IconButton>
                  </Grid>
                </Grid>
                {selectedTableCode === report.TableCode && (
                  <Box sx={{ padding: 1 }}>
                    <StatusDetails details={report.StatusDetails} />
                  </Box>
                )}
              </Paper>
            ))
          ) : (
            <Typography textAlign={"center"}>沒有資料。</Typography>
          )}
        </CardContent>
      </Card>
    </>
  );
}

export default SummaryReport;
